import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { ChevronRight } from '@mui/icons-material'
import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import { Box } from '@mui/system'
import CustomInput from '../../../../../../components/muiComponents/customInput'
import { BoldText } from '../../../../../../layouts/TextStyles'
import { QuestionState } from '../../../../../../features/types'

const styles = {
    questionContent: {
        width: '100%',
        minHeight: '40px',
        backgroundColor: 'rgb(241, 243, 245)',
        borderColor: 'transparent',
        borderRadius: '8px',
        padding: '8px 14px',
        margin: '8px 0',
        display: 'flex',
        alignItems: 'center',
    },

    dialogContent: {
        display: 'flex',
        justifyContent: 'space-between',
        maxHeight: '500px',
        overflowY: 'auto',
    },

    questionBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
}

interface Props {
    open: boolean
    formik: any
    singleQuestion?: QuestionState
    isCreating: boolean
    closeDialog: () => void
    confirmAction: () => void
}

const VideoAnswersDialog: React.FC<Props> = ({
    open,
    confirmAction,
    closeDialog,
    singleQuestion,
    formik,
    isCreating,
}) => {
    const { formatMessage } = useIntl()

    const getQuestionIndex = () => {
        return (
            formik.values.questions.findIndex(
                (e) => e.id === singleQuestion.id,
            ) + 1
        )
    }

    const saveUrl = (e, index: number) => {
        if (singleQuestion) {
            index = formik.values.questions.findIndex(
                (e) =>
                    e.questionId === singleQuestion.questionId ||
                    e.id === singleQuestion.id,
            )
        }
        formik.setFieldValue(`questions[${index}].videoLink`, e.target.value)
    }

    const getSingleQuestionVideoLink = () => {
        return formik.values.questions.find(
            (e) =>
                e.questionId === singleQuestion.questionId ||
                e.id === singleQuestion.id,
        ).videoLink
    }

    useEffect(() => {
        const script = document.createElement('script')

        script.src =
            'https://www.wiris.net/demo/plugins/app/WIRISplugins.js?viewer=image'
        script.async = true

        document.body.appendChild(script)

        return () => {
            document.body.removeChild(script)
        }
    }, [])

    return (
        <Dialog
            scroll="paper"
            maxWidth={'lg'}
            fullWidth
            open={open}
            onClose={closeDialog}
        >
            <DialogTitle>
                {formatMessage({ id: 'videoLessonLinks' })}
            </DialogTitle>

            <DialogContent>
                <TableContainer
                    component={Paper}
                    elevation={0}
                >
                    <Table
                        sx={{
                            width: '100%',
                            '.MuiTableCell-root': { padding: 0 },
                        }}
                        aria-label="custom pagination table"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell width={'45%'}>
                                    {formatMessage({ id: 'questions' })}
                                </TableCell>

                                <TableCell width={'5%'}></TableCell>

                                <TableCell width={'45%'}>
                                    {formatMessage({ id: 'videoLessonLinks' })}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {singleQuestion ? (
                                <TableRow>
                                    <TableCell
                                        component="td"
                                        scope="row"
                                    >
                                        <Box sx={styles.questionBox}>
                                            <Box sx={{ mr: 2 }}>
                                                <BoldText>
                                                    {getQuestionIndex()}
                                                </BoldText>
                                            </Box>

                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: singleQuestion.content.substring(
                                                        0,
                                                        30,
                                                    ),
                                                }}
                                                style={styles.questionContent}
                                            ></p>
                                        </Box>
                                    </TableCell>

                                    <TableCell
                                        component="td"
                                        scope="row"
                                        align="center"
                                    >
                                        <ChevronRight />
                                    </TableCell>

                                    <TableCell
                                        component="td"
                                        scope="row"
                                    >
                                        <CustomInput
                                            fullWidth
                                            placeholder="https://youtu.be/_wR8Q7RGNhE"
                                            value={getSingleQuestionVideoLink()}
                                            onChange={(e) => saveUrl(e, null)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                formik.values.questions.map(
                                    (question, index) => {
                                        return (
                                            <TableRow key={question.questionId}>
                                                <TableCell
                                                    component="td"
                                                    scope="row"
                                                >
                                                    <Box
                                                        key={
                                                            question.id ||
                                                            question.questionId
                                                        }
                                                        sx={styles.questionBox}
                                                    >
                                                        <Box sx={{ mr: 2 }}>
                                                            <BoldText>
                                                                {index + 1}
                                                            </BoldText>
                                                        </Box>

                                                        <p
                                                            dangerouslySetInnerHTML={{
                                                                __html: question.content.substring(
                                                                    0,
                                                                    30,
                                                                ),
                                                            }}
                                                            style={
                                                                styles.questionContent
                                                            }
                                                        ></p>
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    align="center"
                                                >
                                                    <ChevronRight />
                                                </TableCell>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                >
                                                    <Box
                                                        key={
                                                            question.id ||
                                                            question.questionId
                                                        }
                                                    >
                                                        <CustomInput
                                                            fullWidth
                                                            placeholder="https://youtu.be/_wR8Q7RGNhE"
                                                            style={{
                                                                padding: 0,
                                                            }}
                                                            value={
                                                                question.videoLink
                                                            }
                                                            error={
                                                                !question.videoLink
                                                            }
                                                            onChange={(e) =>
                                                                saveUrl(
                                                                    e,
                                                                    index,
                                                                )
                                                            }
                                                        />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    },
                                )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ m: 2 }}>
                {!singleQuestion && (
                    <Button
                        onClick={closeDialog}
                        variant="outlined"
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>
                )}

                <Button
                    variant="contained"
                    disabled={isCreating}
                    onClick={() => {
                        confirmAction()
                    }}
                >
                    {isCreating ? (
                        <CircularProgress
                            size={20}
                            sx={{ mr: 1 }}
                        />
                    ) : (
                        formatMessage({ id: 'save' })
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default VideoAnswersDialog
